import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Instagram, Camera, BarChart3, Megaphone, Users, MessageCircle, CalendarDays, Sparkles, Globe, Zap, Film } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLang } from '../context/LanguageContext';

const translations = {
  fr: {
    pageTitle: 'Menu – Offres | Social Media pour Restaurants - Cocoonut Concept Creations',
    metaDesc: 'Découvrez nos formules de gestion des réseaux sociaux pour restaurants, cafés et hôtels. De l\'Apéro au Dessert, choisissez la formule adaptée à votre établissement.',
    back: 'Retour à l\'accueil',
    brand: 'Cocoonut Concept Creations',
    title: 'Menu – Offres',
    subtitle: 'Choisissez la formule qui correspond le mieux à la visibilité digitale de votre établissement.',
    servicesLabel: 'Services inclus',
    examplesLabel: 'Exemples',
    bottomNote: 'Chaque établissement est unique. Les prestations peuvent être adaptées selon vos besoins.',
    infoTitle: 'Informations',
    infoText: 'TVA non applicable, article 293 B du CGI.',
    seeAlso: 'Découvrez également :',
    portfolio: 'Nos réalisations',
    menuLink: 'Menu des offres',
    contact: 'Contact',
    offers: [
      {
        id: 'apero',
        num: 'I',
        title: 'Apéro',
        price: '350€',
        period: '/ mois',
        description: 'Pour les restaurants, cafés, hôtels et lieux de vie qui souhaitent commencer à améliorer leur présence en ligne.',
        items: [
          { icon: 'Instagram', text: 'Gestion Instagram & Facebook' },
          { icon: 'CalendarDays', text: '1 publication par semaine' },
          { icon: 'Camera', text: '1 Reel par mois' },
          { icon: 'Sparkles', text: 'Création de contenu photo & vidéo simple' },
          { icon: 'BarChart3', text: 'Reporting mensuel simple' },
          { icon: 'Film', text: 'Des Stories Instagram et Facebook peuvent être réalisées selon les besoins de l\'établissement et la stratégie de communication' },
        ],
      },
      {
        id: 'entree',
        num: 'II',
        title: 'Entrée',
        price: '750€',
        period: '/ mois',
        description: 'Pour les restaurants, cafés, hôtels et lieux de vie qui souhaitent développer leur visibilité locale.',
        popular: true,
        popularBadge: 'Le plus choisi',
        popularSub: 'La formule la plus choisie par les établissements.',
        items: [
          { icon: 'Instagram', text: 'Gestion Instagram & Facebook' },
          { icon: 'CalendarDays', text: '2 publications par semaine' },
          { icon: 'Camera', text: 'Création de contenu photo & vidéo (carrousels, Reels, visuels)' },
          { icon: 'BarChart3', text: 'Reporting mensuel' },
          { icon: 'Zap', text: 'Idées de marketing local' },
          { icon: 'Users', text: 'Collaboration avec micro-influenceurs locaux' },
          { icon: 'Megaphone', text: 'Campagnes publicitaires Meta simples' },
          { icon: 'Film', text: 'Des Stories Instagram et Facebook peuvent être publiées régulièrement selon les promotions, les événements ou l\'activité de la semaine' },
        ],
      },
      {
        id: 'plat',
        num: 'III',
        title: 'Plat',
        price: '1000€',
        period: '/ mois',
        description: 'Gestion complète de la communication digitale pour les restaurants, cafés, hôtels et lieux de vie.',
        items: [
          { icon: 'Instagram', text: 'Gestion complète Instagram & Facebook' },
          { icon: 'CalendarDays', text: '2 publications par semaine + Reels réguliers' },
          { icon: 'Camera', text: 'Création de contenu photo & vidéo' },
          { icon: 'MessageCircle', text: 'Gestion des commentaires et messages' },
          { icon: 'BarChart3', text: 'Reporting mensuel détaillé' },
          { icon: 'Zap', text: 'Création de campagnes marketing' },
          { icon: 'Megaphone', text: 'Campagnes publicitaires Meta' },
          { icon: 'Users', text: 'Collaborations avec influenceurs' },
          { icon: 'Sparkles', text: 'Promotion d\'événements ou d\'offres spéciales' },
          { icon: 'Film', text: 'Les Stories sont intégrées à la stratégie de communication afin de mettre en avant l\'activité, les offres et les événements' },
        ],
      },
      {
        id: 'dessert',
        num: 'IV',
        title: 'Dessert',
        price: 'Sur devis',
        period: '',
        isCustom: true,
        description: 'Services complémentaires selon les besoins de l\'établissement.',        items: [
          { icon: 'Globe', text: 'Création de site web' },
          { icon: 'Megaphone', text: 'Campagnes marketing' },
          { icon: 'Sparkles', text: 'Promotion d\'événements' },
          { icon: 'Camera', text: 'Production de contenu supplémentaire' },
          { icon: 'Zap', text: 'Stratégie digitale' },
          { icon: 'BarChart3', text: 'Audit stratégique' },
          { icon: 'Megaphone', text: 'Campagnes publicitaires (Meta Ads / boosting)' },
        ],
      },
    ],
  },
  en: {
    pageTitle: 'Menu – Offers | Social Media for Restaurants - Cocoonut Concept Creations',
    metaDesc: 'Discover our social media management packages for restaurants, cafés and hotels. From Starter to Custom Services, choose the formula that fits your business.',
    back: 'Back to homepage',
    brand: 'Cocoonut Concept Creations',
    title: 'Menu – Offers',
    subtitle: 'Choose the formula that best fits your restaurant\'s digital visibility.',
    servicesLabel: 'Services included',
    examplesLabel: 'Examples',
    bottomNote: 'Every establishment is unique. Services can be adapted depending on your needs.',
    infoTitle: 'Information',
    infoText: 'VAT not applicable, article 293 B of the French Tax Code (CGI).',
    seeAlso: 'See also:',
    portfolio: 'Our work',
    menuLink: 'Service menu',
    contact: 'Contact',
    offers: [
      {
        id: 'apero',
        num: 'I',
        title: 'Starter',
        price: '350€',
        period: '/ month',
        description: 'For restaurants, cafés, hotels and hospitality venues that want to start improving their online presence.',
        items: [
          { icon: 'Instagram', text: 'Instagram & Facebook management' },
          { icon: 'CalendarDays', text: '1 post per week' },
          { icon: 'Camera', text: '1 Reel per month' },
          { icon: 'Sparkles', text: 'Simple photo & video content creation' },
          { icon: 'BarChart3', text: 'Simple monthly reporting' },
          { icon: 'Film', text: 'Instagram and Facebook Stories can be created depending on the needs of the venue and the communication strategy' },
        ],
      },
      {
        id: 'entree',
        num: 'II',
        title: 'Growth',
        price: '750€',
        period: '/ month',
        description: 'For restaurants, cafés, hotels and hospitality venues that want to grow their local visibility.',
        popular: true,
        popularBadge: 'Most popular',
        popularSub: 'Best balance between visibility and content creation.',
        items: [
          { icon: 'Instagram', text: 'Instagram & Facebook management' },
          { icon: 'CalendarDays', text: '2 posts per week' },
          { icon: 'Camera', text: 'Photo & video content creation (carousels, Reels, visuals)' },
          { icon: 'BarChart3', text: 'Monthly reporting' },
          { icon: 'Zap', text: 'Local marketing ideas' },
          { icon: 'Users', text: 'Collaboration with local micro-influencers' },
          { icon: 'Megaphone', text: 'Simple Meta Ads campaigns' },
          { icon: 'Film', text: 'Instagram and Facebook Stories can be published regularly depending on promotions, events or weekly activity' },
        ],
      },
      {
        id: 'plat',
        num: 'III',
        title: 'Full Management',
        price: '1000€',
        period: '/ month',
        description: 'Complete digital communication management for restaurants, cafés, hotels and hospitality venues.',
        items: [
          { icon: 'Instagram', text: 'Full Instagram & Facebook management' },
          { icon: 'CalendarDays', text: '2 posts per week + regular Reels' },
          { icon: 'Camera', text: 'Photo & video content creation' },
          { icon: 'MessageCircle', text: 'Comment and message management' },
          { icon: 'BarChart3', text: 'Detailed monthly reporting' },
          { icon: 'Zap', text: 'Marketing campaign creation' },
          { icon: 'Megaphone', text: 'Meta advertising campaigns' },
          { icon: 'Users', text: 'Influencer collaborations' },
          { icon: 'Sparkles', text: 'Promotion of events or special offers' },
          { icon: 'Film', text: 'Stories are integrated into the communication strategy to highlight activity, offers and events' },
        ],
      },
      {
        id: 'dessert',
        num: 'IV',
        title: 'Custom Services',
        price: 'Custom quote',
        period: '',
        isCustom: true,
        description: 'Additional services depending on the needs of the establishment or venue.',
        items: [
          { icon: 'Globe', text: 'Website creation' },
          { icon: 'Megaphone', text: 'Marketing campaigns' },
          { icon: 'Sparkles', text: 'Event promotion' },
          { icon: 'Camera', text: 'Additional content production' },
          { icon: 'Zap', text: 'Digital strategy' },
          { icon: 'BarChart3', text: 'Strategic audit' },
          { icon: 'Megaphone', text: 'Advertising campaigns (Meta Ads / boosting)' },
        ],
      },
    ],
  },
};

const iconMap = { Instagram, Camera, BarChart3, Megaphone, Users, MessageCircle, CalendarDays, Sparkles, Globe, Zap, Film };

const MenuDivider = () => (
  <div className="flex items-center justify-center gap-4 py-2">
    <div className="h-px w-12 bg-[#CBB279]/40" />
    <div className="w-1.5 h-1.5 rounded-full bg-[#CBB279]/60" />
    <div className="h-px w-12 bg-[#CBB279]/40" />
  </div>
);

const MenuCard = ({ item, index, servicesLabel, examplesLabel }) => {
  const isPopular = item.popular;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative"
      data-testid={`menu-card-${item.id}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-block px-4 py-1 bg-[#CBB279] text-white text-xs uppercase tracking-[0.15em] font-medium rounded-full shadow-sm" data-testid="popular-badge">
            {item.popularBadge}
          </span>
        </div>
      )}
      <div className={`bg-white rounded-sm p-8 md:p-10 hover:shadow-lg transition-shadow duration-500 ${isPopular ? 'border-2 border-[#CBB279]/60 shadow-md' : 'border border-[#243B53]/8'}`}>
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-[0.3em] text-[#7D9BA6] font-medium block mb-3">
            — {item.num} —
          </span>
          <h3 className="font-brand-display text-4xl md:text-5xl text-[#243B53] mb-4">
            {item.title}
          </h3>
          <MenuDivider />
          <div className="mt-4">
            <span className="text-3xl md:text-4xl font-brand-display text-[#CBB279]">
              {item.price}
            </span>
            {item.period && (
              <span className="text-base text-[#7D9BA6] ml-1">{item.period}</span>
            )}
          </div>
          <p className="text-[#243B53]/60 mt-3 text-base max-w-md mx-auto leading-relaxed">
            {item.description}
          </p>
          {isPopular && item.popularSub && (
            <p className="text-[#CBB279] mt-2 text-sm font-medium italic" data-testid="popular-sub">
              {item.popularSub}
            </p>
          )}
        </div>

        <div className="space-y-3 mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7D9BA6] font-medium mb-4 text-center">
            {item.isCustom ? examplesLabel : servicesLabel}
          </p>
          {item.items.map((service, i) => {
            const Icon = iconMap[service.icon];
            return (
              <div key={i} className="flex items-start gap-3 group">
                <Icon
                  size={16}
                  className="text-[#CBB279] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200"
                />
                <span className="text-[#243B53]/75 text-sm leading-relaxed">
                  {service.text}
                </span>
              </div>
            );
          })}
        </div>

        <div className="text-center pt-2">
          <Link
            to="/contact"
            className="inline-block px-8 py-3 border border-[#243B53] text-[#243B53] text-sm uppercase tracking-[0.15em] hover:bg-[#243B53] hover:text-white transition-all duration-300"
            data-testid={`menu-contact-btn-${item.id}`}
          >
            Contact
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const MenuPage = () => {
  const { lang, setLang } = useLang();
  const t = translations[lang];

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="menu-page">
      <Helmet>
        <title>{t.pageTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <link rel="canonical" href={`${window.location.origin}/menu`} />
      </Helmet>

      {/* Header */}
      <div className="bg-[#243B53] pt-8 pb-16 md:pb-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              data-testid="menu-back-link"
            >
              <ArrowLeft size={18} />
              <span className="text-sm">{t.back}</span>
            </Link>

            {/* Language Switcher */}
            <div className="flex items-center text-sm" data-testid="lang-switcher-menu">
              <button
                onClick={() => setLang('fr')}
                className={`transition-colors ${lang === 'fr' ? 'text-[#CBB279] font-semibold' : 'text-white/50 hover:text-white'}`}
              >
                FR
              </button>
              <span className="text-white/30 mx-1.5">|</span>
              <button
                onClick={() => setLang('en')}
                className={`transition-colors ${lang === 'en' ? 'text-[#CBB279] font-semibold' : 'text-white/50 hover:text-white'}`}
              >
                EN
              </button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#CBB279] block mb-6">
              {t.brand}
            </span>
            <h1
              className="font-brand-display text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-tight"
              data-testid="menu-page-title"
            >
              {t.title}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-px w-16 bg-[#CBB279]/50" />
              <div className="w-2 h-2 rounded-full border border-[#CBB279]/60" />
              <div className="h-px w-16 bg-[#CBB279]/50" />
            </div>
            <p className="text-white/70 text-lg md:text-xl leading-relaxed font-light">
              {t.subtitle}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Menu Cards */}
      <div className="container mx-auto px-6 lg:px-8 -mt-8 md:-mt-12 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          {t.offers.map((item, index) => (
            <MenuCard
              key={item.id}
              item={item}
              index={index}
              servicesLabel={t.servicesLabel}
              examplesLabel={t.examplesLabel}
            />
          ))}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16 max-w-lg mx-auto"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-[#243B53]/15" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#CBB279]/50" />
            <div className="h-px w-12 bg-[#243B53]/15" />
          </div>
          <p className="text-[#243B53]/50 text-sm italic leading-relaxed" data-testid="menu-bottom-note">
            {t.bottomNote}
          </p>
        </motion.div>

        {/* Informations Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-10 max-w-lg mx-auto"
          data-testid="menu-info-section"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#7D9BA6] font-medium mb-2">
            {t.infoTitle}
          </p>
          <p className="text-[#243B53]/40 text-xs leading-relaxed">
            {t.infoText}
          </p>
        </motion.div>

        {/* Internal Links for SEO */}
        <div className="mt-12 pt-8 border-t border-[#243B53]/10 max-w-5xl mx-auto">
          <p className="text-sm text-[#7D9BA6] mb-4 text-center">{t.seeAlso}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#portfolio" className="text-[#243B53] hover:text-[#CBB279] transition-colors text-sm font-medium">
              {t.portfolio}
            </Link>
            <span className="text-[#7D9BA6]">·</span>
            <Link to="/contact" className="text-[#243B53] hover:text-[#CBB279] transition-colors text-sm font-medium">
              {t.contact}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
